const ONE_DAY = 86_400_000;
const SEASON_HISTORY_LENGTH = 6;

function localISODate() {
  const now = new Date();

  return new Date(
    now.getTime() -
      now.getTimezoneOffset() *
        60_000
  )
    .toISOString()
    .slice(0, 10);
}

function parseISODate(value) {
  return new Date(
    `${value}T12:00:00`
  );
}

function toISO(date) {
  return new Date(
    date.getTime() -
      date.getTimezoneOffset() *
        60_000
  )
    .toISOString()
    .slice(0, 10);
}

function padDatePart(value) {
  return String(value).padStart(
    2,
    '0'
  );
}

function changeDateByDays(
  value,
  amount
) {
  const date =
    parseISODate(value);

  return toISO(
    new Date(
      date.getTime() +
        amount * ONE_DAY
    )
  );
}

function changeDateYear(
  value,
  nextYear
) {
  const [
    currentYear,
    month,
    day,
  ] = String(value)
    .split('-')
    .map(Number);

  if (
    !Number.isInteger(
      currentYear
    ) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    !Number.isInteger(
      nextYear
    )
  ) {
    return value;
  }

  const finalDayOfMonth =
    new Date(
      nextYear,
      month,
      0
    ).getDate();

  const adjustedDay =
    Math.min(
      day,
      finalDayOfMonth
    );

  return [
    nextYear,
    padDatePart(month),
    padDatePart(
      adjustedDay
    ),
  ].join('-');
}

function getSeasonYears(
  selectedYear
) {
  const currentYear =
    new Date().getFullYear();

  const years =
    Array.from(
      {
        length:
          SEASON_HISTORY_LENGTH,
      },
      (_, index) =>
        currentYear - index
    );

  if (
    Number.isInteger(
      selectedYear
    ) &&
    !years.includes(
      selectedYear
    )
  ) {
    years.push(
      selectedYear
    );
  }

  return years.sort(
    (left, right) =>
      right - left
  );
}

export default function DateRail({
  selectedDate,
  onChange,
  view = 'scores',
  onChangeView,
  updatedLabel = '',
  refreshing = false,
  onRefresh,
  refreshError = '',
}) {
  const selected =
    parseISODate(
      selectedDate
    );

  const today =
    localISODate();

  const isToday =
    selectedDate === today;

  const selectedYear =
    Number(
      selectedDate.slice(
        0,
        4
      )
    );

  const seasonYears =
    getSeasonYears(
      selectedYear
    );

  const compactDateLabel =
    selected.toLocaleDateString(
      undefined,
      {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }
    );

  const accessibleDateLabel =
    selected.toLocaleDateString(
      undefined,
      {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }
    );

  const rangeEnd = parseISODate(
    changeDateByDays(selectedDate, 6)
  );

  const rangeLabel = `${selected.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })} – ${rangeEnd.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })}`;

  let statusLabel =
    updatedLabel;

  if (refreshing) {
    statusLabel =
      'Updating scores…';
  } else if (refreshError) {
    statusLabel =
      'Update failed';
  }

  function handleDateInput(
    event
  ) {
    const nextDate =
      event.target.value;

    if (nextDate) {
      onChange(nextDate);
    }
  }

  function handleSeasonChange(
    event
  ) {
    const nextYear =
      Number(
        event.target.value
      );

    if (
      !Number.isInteger(
        nextYear
      )
    ) {
      return;
    }

    onChange(
      changeDateYear(
        selectedDate,
        nextYear
      )
    );
  }

  return (
    <>
      <div
        className={`mobile-score-date-row${
          isToday
            ? ''
            : ' has-today-action'
        }`}
        aria-label="Scoreboard date range"
      >
        <button
          type="button"
          className={view === 'scores' ? 'active' : ''}
          aria-pressed={view === 'scores'}
          onClick={() => onChangeView?.('scores')}
        >
          <strong>Day</strong>
          <span>{compactDateLabel}</span>
        </button>

        <button
          type="button"
          className={view === 'upcoming' ? 'active' : ''}
          aria-pressed={view === 'upcoming'}
          onClick={() => onChangeView?.('upcoming')}
        >
          <strong>7-Day Schedule</strong>
          <span>{rangeLabel}</span>
        </button>

        {!isToday ? (
          <button
            type="button"
            className="mobile-today-button"
            onClick={() =>
              onChange(today)
            }
            aria-label="Return to today's scoreboard"
          >
            <strong>Today</strong>
          </button>
        ) : null}

        {onRefresh ? <button type="button" className="mobile-score-refresh scores-manual-refresh" onClick={onRefresh} disabled={refreshing} aria-label={view === 'upcoming' ? 'Refresh seven-day schedule' : 'Refresh scores'} aria-busy={refreshing} title={statusLabel}>
          <strong aria-hidden="true">↻</strong><small className="sr-only" role="status">{refreshing ? 'Refreshing' : updatedLabel || 'Refresh'}</small>
        </button> : null}
        <label className="mobile-score-calendar" title="Choose a scoreboard date">
          <span aria-hidden="true">▦</span>
          <span className="sr-only">Choose a scoreboard date</span>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateInput}
            aria-label={`Choose score date. Selected: ${accessibleDateLabel}`}
          />
        </label>
      </div>

      <div className="date-navigation date-navigation-inline date-navigation-desktop">
      <div className="date-inline-primary">
        <button
          className="date-arrow"
          type="button"
          onClick={() =>
            onChange(
              changeDateByDays(
                selectedDate,
                -1
              )
            )
          }
          aria-label="Previous day"
          title="Previous day"
        >
          ‹
        </button>

        <label
          className="date-inline-picker"
          title="Choose a scoreboard date"
        >
          <strong>
            {compactDateLabel}
          </strong>

          <span
            aria-hidden="true"
          >
            ▾
          </span>

          <input
            type="date"
            value={selectedDate}
            onChange={
              handleDateInput
            }
            aria-label={`Selected scoreboard date: ${accessibleDateLabel}`}
          />
        </label>

        <button
          className="date-arrow"
          type="button"
          onClick={() =>
            onChange(
              changeDateByDays(
                selectedDate,
                1
              )
            )
          }
          aria-label="Next day"
          title="Next day"
        >
          ›
        </button>
      </div>

      <div className="date-inline-secondary">
        <label className="season-picker">
          <span className="sr-only">
            Select volleyball
            season
          </span>

          <select
            value={selectedYear}
            onChange={
              handleSeasonChange
            }
            aria-label="Select volleyball season"
          >
            {seasonYears.map(
              (year) => (
                <option
                  key={year}
                  value={year}
                >
                  {year} Season
                </option>
              )
            )}
          </select>
        </label>

        {!isToday ? (
          <button
            type="button"
            className="today-button"
            onClick={() =>
              onChange(today)
            }
          >
            Today
          </button>
        ) : (
          <span
            className="today-indicator"
            aria-label="Viewing today’s scoreboard"
          >
            Today
          </span>
        )}

        {statusLabel ? (
          <span
            className={`date-update-status${
              refreshError
                ? ' error'
                : ''
            }`}
            role="status"
            aria-live="polite"
            title={
              refreshError ||
              undefined
            }
          >
            {statusLabel}
            {onRefresh ? <button className="scores-manual-refresh" type="button" onClick={onRefresh} disabled={refreshing} aria-label={view === 'upcoming' ? 'Refresh seven-day schedule' : 'Refresh scores'} aria-busy={refreshing}><span aria-hidden="true">↻</span></button> : null}
          </span>
        ) : null}
      </div>
      </div>
    </>
  );
}
